import { BoxPage } from '@/components/common/BoxPage'
import { Card, Divider, IconButton, InputAdornment, Typography } from '@mui/material'
import { Button } from '@/@core/components/mui/Button'
import { useNavigate } from 'react-router-dom'
import { FormProvider } from 'antd/es/form/context'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import {
  ActivityLogTypeEnum,
  Company,
  IHttpError,
  IResponse,
  useUpdateBalanceSettingFinanceByCompanyIdMutation
} from '@/@data'
import { FormInput } from '@/components/form'
import { useNotification } from '@/hooks'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import AppLogs from '@/components/common/AppLogs'

export const SettingFinanceDetail = () => {
  const { __ } = useTranslator()
  const navigate = useNavigate()
  const { id } = useParams()
  const n = useNotification()

  const [isSetting, setIsSetting] = useState(true)
  const [isOpenLogs, setIsOpenLogs] = useState(false)

  const schema = yup.object().shape({
    vat_rate: yup
      .number()
      .transform((value, originalValue) => {
        return originalValue === '' || originalValue === null ? undefined : value
      })
      .typeError(__('VAT must be a number'))
      .min(0)
      .max(100)
      .notRequired(),
    service_fee: yup
      .number()
      .transform((value, originalValue) => {
        return originalValue === '' || originalValue === null ? undefined : value
      })
      .typeError(__('Service Fee must be a number'))
      .min(0)
      .notRequired()
  })

  const handleToggleSetting = () => {
    setIsSetting(!isSetting)
  }

  const { data: dataCompany } = useQuery<IResponse<any>>({
    ...Company.getCompanyById(Number(id)),
    enabled: !!id
  })

  useEffect(() => {
    methods.reset({
      vat_rate: Number(dataCompany?.data?.vat_rate) as any,
      service_fee: Number(dataCompany?.data?.service_fee) as any
    })
  }, [dataCompany])

  const methods = useForm({
    defaultValues: {
      vat_rate: 0,
      service_fee: 0
    },
    resolver: yupResolver(schema),
    mode: 'onChange'
  })

  const { mutate: mutateUpdateBalanceSettingFinanceByCompanyId } = useUpdateBalanceSettingFinanceByCompanyIdMutation()

  const onSubmit = async (data: any) => {
    const payload = {
      ...data,
      vat_rate: Number(data.vat_rate),
      service_fee: Number(data.service_fee)
    }

    mutateUpdateBalanceSettingFinanceByCompanyId(
      { data: payload, companyId: Number(id) },
      {
        onSuccess: () => {
          n.success(__('Update balance setting finance by company id success'))
          navigate(`/cms/finance/balance/setting-finance`)
        },
        onError(error: IHttpError) {
          n.error(error.message)
        }
      }
    )
  }

  return (
    <BoxPage
      showBackButton={true}
      title={__('Detail Employer Setting Finance')}
      breadcrumbs={[
        { label: __('Finance Management') },
        { label: __('Balance Management'), href: '/cms/finance/balance' },
        { label: __('Setting Finance'), href: '/cms/finance/balance/setting-finance' },
        { label: __('Detail Employer Setting Finance') }
      ]}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className='flex justify-between items-start gap-5'>
            <Card sx={{ borderRadius: 3, flex: 1 }}>
              <div className='p-4'>
                <div className='flex justify-between items-center'>
                  <div className='flex items-center gap-3'>
                    <Button
                      variant='outlined'
                      size='small'
                      className='border-blue-500 text-blue-600 hover:border-blue-600'
                    >
                      <span className='text-[15px] font-medium max-w-[500px] whitespace-nowrap overflow-hidden text-ellipsis' title={dataCompany?.data?.name || '-'}>{dataCompany?.data?.name || '-'}</span>
                    </Button>
                    <Divider orientation='vertical' flexItem className='w-[2px] bg-[#2f2b3d1f]' />
                    <Typography variant='h6' className='text-[18px] font-medium'>
                      {__('Setting Finance Employer')}
                    </Typography>
                  </div>
                  <div className='flex items-center gap-2'>
                    <IconButton
                      size='small'
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: '#f5f5f5',
                        '&:hover': {
                          backgroundColor: '#e0e0e0'
                        }
                      }}
                      onClick={() => setIsOpenLogs(true)}
                    >
                      <i className='tabler-history text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size='small'
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: '#f5f5f5',
                        '&:hover': {
                          backgroundColor: '#e0e0e0'
                        }
                      }}
                      onClick={() => {
                        navigate(`/cms/finance/balance/setting-finance`)
                      }}
                    >
                      <i className='tabler-x text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size='small'
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: '#f5f5f5',
                        '&:hover': {
                          backgroundColor: '#e0e0e0'
                        }
                      }}
                      onClick={() => {
                        handleToggleSetting()
                      }}
                    >
                      <i className='tabler-viewport-wide text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                    </IconButton>
                  </div>
                </div>
                <div className='text-[15px] font-medium text-[#2f2b3db3] mt-3'>
                  {__('This section allows you to adjust the VAT and define Fee Charges')}
                </div>

                <div className='my-5'>
                  <Typography variant='h6' className='text-[18px] font-medium'>
                    {__('Setting Company Attendance')}
                  </Typography>
                </div>
                <div className='grid grid-cols-2 gap-6'>
                  <div>
                    <FormInput
                      name='service_fee'
                      label={__('Service Fee')}
                      placeholder={__('Enter Service Fee')}
                      control={methods.control}
                      slotProps={{
                        input: {
                          endAdornment: <InputAdornment position='end'>&#8363;</InputAdornment>
                        }
                      }}
                      className='w-full'
                      onClear={() => {
                        methods.setValue('service_fee', undefined)
                      }}
                    />
                  </div>
                  <div>
                    <FormInput
                      name='vat_rate'
                      label={__('VAT')}
                      placeholder={__('Enter VAT')}
                      control={methods.control}
                      slotProps={{
                        input: {
                          endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                          onInput: (e: any) => {
                            if (e.target.value < 0) {
                              e.target.value = 0
                            }

                            if (e.target.value > 100) {
                              e.target.value = 100
                            }
                          }
                        }
                      }}
                      className='w-full'
                      onClear={() => {
                        methods.setValue('vat_rate', undefined)
                      }}
                    />
                  </div>
                </div>
              </div>
            </Card>
            {isSetting && (
              <Card sx={{ borderRadius: 3, width: '266px' }}>
                <div className='p-4'>
                  <Button
                    variant='tonal'
                    type='button'
                    size='small'
                    className='block w-full mb-3'
                    onClick={() => {
                      methods.reset()
                    }}
                  >
                    <span className='text-[15px] font-medium'>{__('Reset')}</span>
                  </Button>
                  <Button type='submit' variant='tonal' size='small' className='block w-full bg-[#28c76f29]'>
                    <span className='text-[15px] font-medium text-[#28c76f]'>{__('Save')}</span>
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </form>
      </FormProvider>
      <AppLogs
        open={isOpenLogs}
        setOpen={setIsOpenLogs}
        title={__('History Menu Setting Finance Detail')}
        object_type={ActivityLogTypeEnum.SettingFinance}
        companyId={Number(id)}
      />
    </BoxPage>
  )
}
